import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import type { FC } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';

import EditIcon from '@material-ui/icons/Edit';
import { DayAreas } from 'src/interfaces/new-channel-partner/channelPartner';
import { ConfirmationDialog } from 'src/components/confirmation-dialog';
import { useDialog } from 'src/hooks/use-dialog';
import { getDayOfWeek } from 'src/utils/get-day';
import { InputField } from 'src/components/input-field';
import { ChevronLeft as ChevronLeftIcon } from '../../../../../icons/chevron-left';
import { ChevronDown as ChevronDownIcon } from '../../../../../icons/chevron-down';
import { Trash as TrashIcon } from '../../../../../icons/trash';
import { AddEWastePincodeDay } from '../../dialogs/eWastePincodeDay';

interface EWastePincodeDayProps {
  pincodeDays: DayAreas[];
  serviceIds: string[];
  addPincodeDay?: (data: DayAreas) => Promise<boolean>;
  updatePincodeDay?: (data: DayAreas) => Promise<boolean>;
  deletePincodeDay: (data: number) => void;
}

interface PincodeAreas {
  day: number;
  pincode: number;
  areas: string[]
}

export const ShowEWastePincodeDayCard: FC<EWastePincodeDayProps> = (props) => {
  const { pincodeDays, serviceIds, addPincodeDay, updatePincodeDay, deletePincodeDay } = props;
  const [serviceToBeDeleted, setServiceToBeDeleted] = useState<DayAreas>();
  const [productToBeEdit, setProductToBeEdit] = useState<DayAreas>();
  const [displayInfo, setDisplayInfo] = useState<boolean>(true);
  const [openEWasteServiceAreaDialog, setOpenEWasteServiceAreaDialog] = useState<boolean>(false);
  const [uniqueCodesAreas, setUniqueCodesAreas] = useState<PincodeAreas[]>([]);
  const [pincodeToBeSearch, setPincodeToBeSearch] = useState<string>('');
  const [filteredCodesAreas, setFilteredCodesAreas] = useState<PincodeAreas[]>([]);

  const [isAdd, setIsAdd] = useState<boolean>(false);

  const serviceToBeDeleteDetails = (brand: DayAreas) => {
    setServiceToBeDeleted(brand);
  };
  const [
    deleteDialogOpen,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog
  ] = useDialog();

  useEffect(() => {
    if (serviceToBeDeleted) {
      handleOpenDeleteDialog();
    }
  }, [serviceToBeDeleted]);

  const countRecords = (day: number) => filteredCodesAreas.filter(
    (item) => item.day === day
  )?.length;

  const getDay = (index: number) => {
    if (index === 0) {
      return getDayOfWeek(filteredCodesAreas[index].day);
    }
    if (filteredCodesAreas[index].day === filteredCodesAreas[index - 1].day) {
      return '';
    }
    return getDayOfWeek(filteredCodesAreas[index].day);
  };

  const preparePincodeDayData = (dayAreas: DayAreas[]) => {
    let tempArray: PincodeAreas[] = [];
    for (let i = 0; i < dayAreas.length; i++) {
      const pincodes = new Set();
      dayAreas[i].areas?.forEach((item) => {
        const [pincode] = item.split('#');
        pincodes.add(pincode);
      });
      const pincodeArray = Array.from(pincodes);
      for (let j = 0; j < pincodeArray.length; j++) {
        const result = dayAreas[i].areas?.filter((s) => s.startsWith(`${pincodeArray[j]}`));
        const newAreas: string[] = [];
        result.forEach((it) => {
          const area = it.split('#')[1];
          newAreas.push(area);
        });
        tempArray.push({
          day: dayAreas[i].day,
          pincode: pincodeArray[j] as number,
          areas: newAreas
        });
      }
    }
    tempArray = tempArray.sort(
      // eslint-disable-next-line no-nested-ternary
      (a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : a.pincode < b.pincode ? -1 : 1)
    );
    setUniqueCodesAreas(tempArray);
    setFilteredCodesAreas(tempArray);
  };

  useEffect(() => {
    if (pincodeDays?.length > 0) {
      preparePincodeDayData(pincodeDays);
    }
  }, [pincodeDays]);

  const handleDeleteService = async () => {
    handleCloseDeleteDialog();
    await deletePincodeDay(serviceToBeDeleted.day);
  };

  const filterPincodes = () => {
    if (!Number.isNaN(Number(pincodeToBeSearch))) {
      const filterData = uniqueCodesAreas.filter(
        (obj) => obj.pincode.toString().includes(pincodeToBeSearch)
      );
      setFilteredCodesAreas(filterData);
    } else {
      setFilteredCodesAreas(uniqueCodesAreas);
    }
  };

  useEffect(() => {
    if (pincodeToBeSearch !== '') {
      filterPincodes();
    }
  }, [pincodeToBeSearch]);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mt: 1
        }}
      >
        <CardHeader
          titleTypographyProps={{ variant: 'h2' }}
          title="Pincode Day Mapping"
          action={(
            <>
              <Button
                color="primary"
                size="small"
                onClick={() => {
                  setIsAdd(true);
                  setOpenEWasteServiceAreaDialog(true);
                }}
                variant="contained"
              >
                Add
              </Button>
              { pincodeDays?.length > 0 && (
                <>
                  { displayInfo && (
                  <IconButton
                    sx={{ py: 0 }}
                    aria-label="view"
                    onClick={() => {
                      setDisplayInfo(false);
                    }}
                  >
                    <ChevronDownIcon />
                  </IconButton>
                  )}
                  { !displayInfo && (
                  <IconButton
                    sx={{ py: 0 }}
                    aria-label="view"
                    onClick={() => {
                      setDisplayInfo(true);
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  )}
                </ >
              )}
            </>
          )}
        />
        {displayInfo && (
          <>
            {pincodeDays?.length > 0 && (
            <CardContent>
              <Grid
                container
                spacing={1}
              >
                <Grid
                  item
                  md={4}
                  xs={12}
                >
                  <InputField
                    fullWidth
                    placeholder="Search By Pincode"
                    name="pincodeToBeSearch"
                    onChange={(event) => setPincodeToBeSearch(event.target.value)}
                    sx={{ mb: 1 }}
                    value={pincodeToBeSearch}
                  />
                </Grid>
                <Grid
                  item
                  md={12}
                  xs={12}
                  sm={12}
                >
                  <Card
                    variant="outlined"
                  >
                    <TableContainer
                      sx={{
                        maxHeight: '220px',
                      }}
                    >
                      <Table
                        size="small"
                        stickyHeader
                        aria-label="sticky table"
                      >
                        <TableHead>
                          <TableRow
                            key="brandTable"
                          >
                            <TableCell
                              width="10%"
                              sx={{
                                backgroundColor: 'neutral.100',
                              }}
                            >
                              Day
                            </TableCell>
                            <TableCell
                              width="15%"
                              sx={{
                                backgroundColor: 'neutral.100',
                              }}
                            >
                              Pincode
                            </TableCell>
                            <TableCell
                              width="60%"
                              sx={{
                                backgroundColor: 'neutral.100',
                              }}
                            >
                              Area
                            </TableCell>
                            <TableCell
                              align="center"
                              width="15%"
                              sx={{
                                backgroundColor: 'neutral.100',
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredCodesAreas?.map((item, index) => (
                            <TableRow
                              key={`${item.day}${item.pincode}`}
                            >
                              {getDay(index) !== '' && (
                              <TableCell
                                rowSpan={countRecords(item.day)}
                              >
                                {getDay(index)}
                              </TableCell>
                              )}
                              <TableCell>
                                {item.pincode}
                              </TableCell>
                              <TableCell>
                                {item.areas?.map((pincode, codeIndex) => (
                                  (codeIndex !== item.areas.length - 1) ? `${pincode}, ` : `${pincode}`
                                ))}
                              </TableCell>
                              {getDay(index) !== '' && (
                              <TableCell
                                align="center"
                                rowSpan={countRecords(item.day)}
                              >
                                <Box>
                                  <IconButton
                                    title="Edit Pincode of this Day"
                                    aria-label="edit"
                                    onClick={() => {
                                      setProductToBeEdit(
                                        pincodeDays.filter((value) => value.day === item.day)[0]
                                      );
                                      setIsAdd(false);
                                      setOpenEWasteServiceAreaDialog(true);
                                    }}
                                    style={{ padding: 0, marginRight: 2 }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  {' '}
                                  |
                                  {' '}
                                  <IconButton
                                    title="Delete Pincode and Area of this Day"
                                    color="primary"
                                    onClick={() => {
                                      serviceToBeDeleteDetails(
                                        pincodeDays.filter((value) => value.day === item.day)[0]
                                      );
                                    }}
                                    type="button"
                                    style={{ padding: 0 }}
                                  >
                                    <TrashIcon />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                    </TableContainer>

                  </Card>
                </Grid>
              </Grid>
            </CardContent>
            )}
          </ >
        )}
      </Card>
      { openEWasteServiceAreaDialog && (
      <AddEWastePincodeDay
        isAdd={isAdd}
        open={openEWasteServiceAreaDialog}
        onClose={() => setOpenEWasteServiceAreaDialog(false)}
        cpServiceAreaIds={serviceIds}
        pincodeDays={isAdd ? pincodeDays : [productToBeEdit]}
        addPincodeDay={addPincodeDay}
        updatePincodeDay={updatePincodeDay}
      />
      )}
      <ConfirmationDialog
        message="Are you really want to delete the Day and its associated pincodes?"
        onCancel={() => {
          handleCloseDeleteDialog();
        }}
        onConfirm={handleDeleteService}
        open={deleteDialogOpen}
        title="Are You Sure?"
        variant="error"
      />
    </>
  );
};

ShowEWastePincodeDayCard.propTypes = {
  pincodeDays: PropTypes.array,
  addPincodeDay: PropTypes.func,
  serviceIds: PropTypes.array,
  updatePincodeDay: PropTypes.func,
  deletePincodeDay: PropTypes.func
};
